// ---------------------------------------------------------------------------
// Renderer: Tile Row
//
// Renders a row (or grid) of metric tiles with value + label.
// Supports grid mode (with cols), single-row mode, scale groups for
// consistent font sizing across tiles, and top-border accents.
// ---------------------------------------------------------------------------

import Konva from "konva";
import CustomText from "../CustomText";
import { themeColor, defaultTheme } from "../renderers/plainObjectRenderer";
import { formatValue } from "../shared/formatting.js";
import { evaluateFormula } from "../shared/formula-evaluator.js";
import { evaluateConditionalColor } from "../shared/condition-evaluator.js";

// Cross-renderer helpers — extracted to ui/src/renderers/helpers.js
import {
  normalizeTile,
  resolveTileScaleGroups,
  tagEvidence,
  addDebugOverlay,
  measureTextNaturalWidth
} from "./helpers.js";

// Provenance marker for a tile's value: "*" when the value (or, for formula
// tiles, any input) was derived by the pipeline rather than reported directly;
// "†" when it comes from a sub-annual (partial) period. The crystallizer
// stamps `_provenance` on the block's data from the source details' flags.
// Template formulas over clean reported inputs are NOT marked — the flag is
// for provenance the viewer can't otherwise see.
function tileProvenanceMarker(tile, dataContext) {
  const prov = dataContext?._provenance;
  if (!prov) return null;
  const keys = tile.value?.extract
    ? [tile.value.extract]
    : tile.value?.formula
      ? (String(tile.value.formula).match(/[a-zA-Z_][a-zA-Z0-9_]*/g) || [])
      : [];
  let derived = false, partial = false, periodMonths;
  for (const key of keys) {
    const p = prov[key];
    if (!p) continue;
    if (p.derived) derived = true;
    if (p.partial) { partial = true; periodMonths = periodMonths ?? p.period_months; }
  }
  if (!derived && !partial) return null;
  return { marker: `${derived ? "*" : ""}${partial ? "†" : ""}`, derived, partial, periodMonths };
}

function buildProvenanceFootnote(notes) {
  const parts = [];
  if (notes.some(n => n.derived)) parts.push("* derived from reported figures");
  const partialNote = notes.find(n => n.partial);
  if (partialNote) {
    parts.push(partialNote.periodMonths
      ? `† partial period (${partialNote.periodMonths}M) — not full year`
      : "† partial period — not full year");
  }
  return parts.join("   ");
}

export function renderTileRow(block, crystalizedData, options) {
  const { x = 0, y = 0, width = 600, height: allocH, imageCallback, theme, fontScale = 1 } = options;
  const s = fontScale;
  const themeColors = theme || defaultTheme;
  const tiles = block.tiles || [];
  const dataContext = crystalizedData[block.id] || {};
  const group = new Konva.Group({ x, y, width });
  const provenanceNotes = [];
  // Pre-compute markers so the footnote can reserve space INSIDE the height
  // allocation — drawing below the grid gets clipped by the band boundary.
  const tileProvs = tiles.map(t => tileProvenanceMarker(t, dataContext));
  const footnoteH = tileProvs.some(Boolean) ? Math.round(13 * s) : 0;
  // Draws the marker as a small superscript beside the value (never appended
  // to the value string — it would inherit the big bold value font).
  const addMarkerNode = (prov, valueX, valueY, valueText, valueFontSize, maxW) => {
    const vw = measureTextNaturalWidth({ text: String(valueText), fontSize: valueFontSize, fontStyle: "bold" });
    group.add(new CustomText({
      x: valueX + Math.min(vw, maxW) + Math.round(2 * s), y: valueY,
      width: Math.round(22 * s),
      text: prov.marker, fontSize: Math.max(8, Math.round(valueFontSize * 0.45)),
      fill: "#888", refreshCallback: imageCallback
    }));
  };

  // Grid mode: cols specified → wrap tiles into multi-row card grid
  if (block.cols && tiles.length > 0) {
    const cols = Math.max(1, Math.min(block.cols, tiles.length));
    const rows = Math.ceil(tiles.length / cols);
    const gap = Math.round(10 * s);
    const tileW = (width - gap * (cols - 1)) / cols;
    const minTileH = Math.round(80 * s);
    const allocFromRows = allocH && allocH > 0 && allocH < 99999
      ? (allocH - footnoteH - (rows - 1) * gap) / rows
      : minTileH;
    // Don't clamp to minTileH when an explicit allocation was provided — let
    // the caller's height budget win so the grid never overflows sibling blocks.
    const tileH = allocFromRows < minTileH
      ? Math.max(1, Math.floor(allocFromRows))
      : Math.floor(allocFromRows);
    const tileBg = block.tile_background || "#F5F1E8";

    const tilePad = Math.round(16 * s);
    const innerW = tileW - tilePad * 2;
    const valueTopPad = Math.round(14 * s);

    const groupScales = resolveTileScaleGroups({
      tiles, dataContext, blockNullFallback: block.null_fallback,
      fontScale: s, innerW, resetGroups: block.resetGroups
    });

    // Pre-pass: find the smallest font that fits ALL callout tiles so they share one size.
    const maxTextH = tileH - valueTopPad - tilePad;
    const calloutFsMax = Math.round(13 * s);
    const calloutFsMin = Math.round(9 * s);
    let sharedCalloutFs = calloutFsMax;
    for (const tile of tiles) {
      if (tile.style !== "callout") continue;
      const raw = tile.text?.extract ? dataContext[tile.text.extract] : "";
      const textVal = typeof raw === "string" && raw.trim() ? raw : "";
      if (!textVal) continue;
      let fs = sharedCalloutFs;
      while (fs > calloutFsMin) {
        const probe = new CustomText({ x: 0, y: 0, width: innerW, text: textVal, fontSize: fs, lineHeight: 1.5 });
        const fits = probe.height() <= maxTextH;
        probe.destroy();
        if (fits) break;
        fs -= Math.round(1 * s);
      }
      sharedCalloutFs = Math.min(sharedCalloutFs, Math.max(calloutFsMin, fs));
    }

    for (let i = 0; i < tiles.length; i++) {
      const tile = tiles[i];
      const row = Math.floor(i / cols);
      const col = i % cols;
      const tx = col * (tileW + gap);
      const ty = row * (tileH + gap);

      // Callout tile: full-width body statement, no large value/label structure.
      if (tile.style === "callout") {
        const raw = tile.text?.extract ? dataContext[tile.text.extract] : "";
        const textVal = typeof raw === "string" && raw.trim() ? raw : (block.null_fallback?.display || "");
        group.add(new Konva.Rect({
          x: tx, y: ty, width: tileW, height: tileH,
          fill: tileBg, cornerRadius: Math.round(4 * s)
        }));
        if (block.top_border) {
          const borderColor = block.top_border.color || "#2D8659";
          const borderH = Math.round((block.top_border.width || 4) * s);
          group.add(new Konva.Rect({
            x: tx, y: ty, width: tileW, height: borderH,
            fill: borderColor, cornerRadius: [Math.round(4 * s), Math.round(4 * s), 0, 0]
          }));
        }
        const calloutNode = new CustomText({
          x: tx + tilePad, y: ty + valueTopPad,
          width: innerW,
          text: textVal, fontSize: sharedCalloutFs, lineHeight: 1.5,
          fill: textVal ? "#1a1a1a" : "#999",
          refreshCallback: imageCallback
        });
        group.add(calloutNode);
        if (tile.text?.extract) tagEvidence(calloutNode, `${block.id}.${tile.text.extract}`, options);
        continue;
      }

      const norm = normalizeTile(tile, dataContext, block.null_fallback);
      const prov = tileProvs[i];
      const showMarker = prov && !norm.isNull;
      if (showMarker) provenanceNotes.push(prov);

      group.add(new Konva.Rect({
        x: tx, y: ty, width: tileW, height: tileH,
        fill: tileBg, cornerRadius: Math.round(4 * s)
      }));

      if (block.top_border) {
        const borderColor = block.top_border.color || "#2D8659";
        const borderH = Math.round((block.top_border.width || 4) * s);
        group.add(new Konva.Rect({
          x: tx, y: ty, width: tileW, height: borderH,
          fill: borderColor, cornerRadius: [Math.round(4 * s), Math.round(4 * s), 0, 0]
        }));
      }

      const valueFontMax = Math.round(26 * s);
      let valueFontSize = valueFontMax;
      if (norm.valueScaleGroup && groupScales.has(norm.valueScaleGroup)) {
        valueFontSize = Math.round(valueFontMax * groupScales.get(norm.valueScaleGroup));
      } else {
        const valueFontMin = Math.round(10 * s);
        let probe;
        while (true) {
          probe = new CustomText({
            x: 0, y: 0, width: innerW,
            text: String(norm.displayValue), fontSize: valueFontSize, fontStyle: "bold"
          });
          const lineCount = (probe.textArr && probe.textArr.length) || 1;
          probe.destroy();
          if (lineCount <= 1 || valueFontSize <= valueFontMin) break;
          valueFontSize -= Math.round(2 * s);
        }
      }
      const valueNode = new CustomText({
        x: tx + tilePad, y: ty + valueTopPad,
        width: innerW,
        text: String(norm.displayValue), fontSize: valueFontSize, fontStyle: "bold",
        fill: norm.valueColor, align: "left", refreshCallback: imageCallback
      });
      group.add(valueNode);
      if (showMarker) {
        addMarkerNode(prov, tx + tilePad, ty + valueTopPad, norm.displayValue, valueFontSize, innerW);
      }
      if (tile.value?.extract) tagEvidence(valueNode, `${block.id}.${tile.value.extract}`, options);
      const valueH = valueNode.height();

      const labelFontMax = Math.round(11 * s);
      let labelFontSize = labelFontMax;
      if (norm.labelScaleGroup && groupScales.has(norm.labelScaleGroup)) {
        labelFontSize = Math.round(labelFontMax * groupScales.get(norm.labelScaleGroup));
      }
      group.add(new CustomText({
        x: tx + tilePad, y: ty + valueTopPad + valueH + Math.round(4 * s),
        width: innerW,
        text: norm.labelText, fontSize: labelFontSize, fill: "#777",
        withMarkdown: /\*\*[^*]+\*\*|_[^_]+_/.test(String(norm.labelText)),
        align: "left", refreshCallback: imageCallback
      }));
    }

    let gridH = rows * tileH + (rows - 1) * gap;
    if (provenanceNotes.length) {
      group.add(new CustomText({
        x: 0, y: gridH + Math.round(2 * s), width,
        text: buildProvenanceFootnote(provenanceNotes),
        fontSize: Math.round(8.5 * s), fill: "#888", fontStyle: "italic",
        refreshCallback: imageCallback
      }));
      gridH += footnoteH;
    }
    group.height(gridH);
    addDebugOverlay(group, block, { ...options, height: gridH });
    return group;
  }

  // Single-row mode (legacy)
  const tileWidth = (width - Math.round(16 * s)) / Math.max(tiles.length, 1);
  const naturalRowH = Math.round(72 * s);
  const rowHeight = (allocH && allocH > 0 && allocH < 99999 ? Math.floor(allocH) : naturalRowH) - footnoteH;

  group.add(new Konva.Rect({ width, height: rowHeight, fill: block.background || "#F5F1E8", cornerRadius: Math.round(6 * s) }));

  for (let i = 0; i < tiles.length; i++) {
    const tile = tiles[i];
    const tx = Math.round(8 * s) + i * tileWidth;
    const norm = normalizeTile(tile, dataContext, block.null_fallback);
    const prov = tileProvs[i];
    const showMarker = prov && !norm.isNull;
    if (showMarker) provenanceNotes.push(prov);

    group.add(new CustomText({
      x: tx, y: Math.round(8 * s), width: tileWidth,
      text: norm.labelText, fontSize: Math.round(10 * s), fill: "#777",
      align: "center", refreshCallback: imageCallback
    }));

    const rowValueFs = Math.round(22 * s);
    group.add(new CustomText({
      x: tx, y: Math.round(26 * s), width: tileWidth,
      text: String(norm.displayValue), fontSize: rowValueFs, fontStyle: "bold",
      fill: norm.valueColor, align: "center", refreshCallback: imageCallback
    }));
    if (showMarker) {
      // Value is centre-aligned — place the marker just past its right edge.
      const vw = measureTextNaturalWidth({ text: String(norm.displayValue), fontSize: rowValueFs, fontStyle: "bold" });
      group.add(new CustomText({
        x: tx + Math.round(tileWidth / 2 + Math.min(vw, tileWidth) / 2 + 2 * s), y: Math.round(26 * s),
        width: Math.round(22 * s),
        text: prov.marker, fontSize: Math.max(8, Math.round(rowValueFs * 0.45)),
        fill: "#888", refreshCallback: imageCallback
      }));
    }

    if (i < tiles.length - 1) {
      group.add(new Konva.Line({
        points: [tx + tileWidth, Math.round(10 * s), tx + tileWidth, rowHeight - Math.round(10 * s)],
        stroke: "#e0dcd0", strokeWidth: 1
      }));
    }
  }

  let totalH = rowHeight;
  if (provenanceNotes.length) {
    group.add(new CustomText({
      x: Math.round(8 * s), y: rowHeight + Math.round(2 * s), width: width - Math.round(16 * s),
      text: buildProvenanceFootnote(provenanceNotes),
      fontSize: Math.round(8.5 * s), fill: "#888", fontStyle: "italic",
      refreshCallback: imageCallback
    }));
    totalH += footnoteH;
  }
  group.height(totalH);
  addDebugOverlay(group, block, { ...options, height: totalH });
  return group;
}
